import { ROUTES } from '@/shared/constants/routesConstants';

export const US_MARKET = 'US';
export const MX_MARKET = 'MX';
export const GT_MARKET = 'GT';
export const RU_MARKET = 'RU';
export const IN_MARKET = 'IN';
export const CO_MARKET = 'CO';

// TODO: Remove this mapping once the US market backend is deployed.
// US currently proxies to MX APIs as a temporary measure.
export const API_COUNTRY_CODE_MAP = {};

export const DEFAULT_TAB_CONFIG = {
  tabs: ['online', 'offline'],
  labels: ['Online', 'Offline'],
};

export const MARKET_TAB_CONFIG = {
  [US_MARKET]: {
    tabs: ['online', 'refunds'],
    labels: ['Online', 'Refunds'],
    onlineOrderColumns: {
      visibleColumns: ['orderId', 'orderState', 'category', 'createdBy', 'orderDate', 'deliveryDate', 'updatedDate'],
      columnLabelOverrides: {
        orderState: 'Status',
      },
    },
    refundsColumns: {
      visibleColumns: ['orderId', 'storeId', 'createdDate', 'orderState', 'refundRequestedQty', 'refundApprovedQty', 'refundAmount', 'refundReason'],
      columnLabelOverrides: {
        orderState: 'Status',
      },
    },
  },
  [MX_MARKET]: {
    tabs: ['online', 'offline', 'rewards'],
    labels: ['Online', 'Offline', 'Rewards'],
    onlineOrderColumns: {
      visibleColumns: ['orderId', 'orderState', 'orderSubState', 'orderDate', 'storeId', 'soldCustomerId', 'items', 'orderTotal', 'deliveryDate', 'updatedDate', 'localDeliveryDate'],
    },
  },
  [GT_MARKET]: {
    tabs: ['online', 'offline', 'rewards'],
    labels: ['Online', 'Offline', 'Rewards'],
  },
  [RU_MARKET]: {
    tabs: ['online', 'offline'],
    labels: ['Online', 'Offline'],
    visibleItemColumns: ['item', 'productId', 'upcNumber', 'deliveredQuantity', 'unitPrice', 'materialUom', 'grandTotal'],
  },
};

// Tab keys shown on the online order detail page by default (all markets not listed below).
export const ONLINE_ORDER_DETAIL_DEFAULT_TABS = [
  'order-info', 'items', 'payment-credits', 'sold-to', 'digital-payment', 'rewards',
];

// Per-market overrides for online order detail tabs.
// Omit a market to use ONLINE_ORDER_DETAIL_DEFAULT_TABS.
export const ONLINE_ORDER_DETAIL_TABS_BY_MARKET = {
  [US_MARKET]: ['order-info', 'items', 'payment-credits', 'sold-to', 'refunds'],
};

// Tabs shown on the rewards order detail page. Unlike the online/offline detail
// pages, this is NOT market-specific — every market gets the same three tabs.
export const REWARDS_ORDER_DETAIL_TABS = ['order-info', 'payment-credits', 'sold-to'];

// Markets that render the MODERN order-detail screen; all others render the
// legacy detail screen. This is a per-market CONTENT decision and is intentionally
// INDEPENDENT of VITE_MODERN_UI (which controls the colour THEME only). Add a
// market here as it migrates to the modern detail screen.
export const MODERN_ORDER_DETAIL_MARKETS = [US_MARKET];

export const isModernOrderDetail = (countryCode) =>
  MODERN_ORDER_DETAIL_MARKETS.includes(countryCode);

// ── Delivery tabs per market ────────────────────────────────────────────────
// Declarative per-market delivery tab config, mirroring MARKET_TAB_CONFIG (order
// tabs). Each market's full tab list is explicit. A route not present in a
// market's list is treated as invalid for that market — see isValidDeliveryRoute,
// which DeliveryLayout uses to 404 out-of-market deep links (parity with the
// orders list, which 404s invalid-for-market tab types).
const MOV_TAB = { label: 'MOV', icon: 'description', path: ROUTES.DELIVERY_MOV };
const EXPRESS_MOA_TAB = {
  label: 'Express Delivery MOA',
  icon: 'description',
  path: ROUTES.DELIVERY_EXPRESS_MOA,
};

export const DEFAULT_DELIVERY_TABS = [
  { label: 'Overview', icon: 'dashboard',      path: ROUTES.DELIVERY_OVERVIEW },
  { label: 'Routes',   icon: 'route',          path: ROUTES.DELIVERY_ROUTES },
  { label: 'Holidays', icon: 'calendar_today', path: ROUTES.DELIVERY_HOLIDAYS },
  { label: 'MOA',      icon: 'description',     path: ROUTES.DELIVERY_MOA },
];

export const DELIVERY_TABS_BY_MARKET = {
  // Routes is hidden for US — not applicable to that market's delivery workflow.
  [US_MARKET]: [
    { label: 'Overview', icon: 'dashboard',      path: ROUTES.DELIVERY_OVERVIEW },
    { label: 'Holidays', icon: 'calendar_today', path: ROUTES.DELIVERY_HOLIDAYS },
    MOV_TAB,
  ],
  [GT_MARKET]: [
    { label: 'Overview', icon: 'dashboard',      path: ROUTES.DELIVERY_OVERVIEW },
    { label: 'Routes',   icon: 'route',          path: ROUTES.DELIVERY_ROUTES },
    { label: 'Holidays', icon: 'calendar_today', path: ROUTES.DELIVERY_HOLIDAYS },
    MOV_TAB,
  ],
  [IN_MARKET]: [MOV_TAB],
  [CO_MARKET]: [
    ...DEFAULT_DELIVERY_TABS,
    EXPRESS_MOA_TAB,
  ],
};

export const getDeliveryTabs = (countryCode) =>
  DELIVERY_TABS_BY_MARKET[countryCode] ?? DEFAULT_DELIVERY_TABS;

// True when `pathname` is a valid delivery route for `countryCode` (i.e. it is one
// of that market's tabs). NOTE: routes with no tab in any market (e.g.
// /delivery/events) are invalid for all markets by this rule.
export const isValidDeliveryRoute = (countryCode, pathname) =>
  getDeliveryTabs(countryCode).some((tab) => tab.path === pathname);

// ── Delivery overview 1P/3P country-code filter ─────────────────────────────
// Per-market options for the channel filter on the Delivery Overview screen.
// Selecting an option overrides the countryCode header on the fetchAllDates
// list API call only; all other calls keep the shell-provided code.
// A market not listed here renders no filter.
export const DELIVERY_OVERVIEW_COUNTRY_OPTIONS = {
  [CO_MARKET]: [
    { label: '1P', value: CO_MARKET },
    { label: '3P', value: 'CO3P' },
  ],
};

export const getDeliveryOverviewCountryOptions = (countryCode) =>
  DELIVERY_OVERVIEW_COUNTRY_OPTIONS[countryCode] ?? null;

export const getOrderCountryCode = (countryCode, orderType) =>
  countryCode === 'ES' && orderType === '3P' ? 'ES3P' : countryCode;
